import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { useTranslation } from 'react-i18next'

import { Box, HStack, Text } from '@enechain/ecloud-designsystem'

import { IssuerAreaStatus } from '~/components/bidding/common/IssuerSelector'
import type { IssuerOption } from '~/components/bidding/common/bidIssuer'
import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { DsSelect } from '~/components/common/DsSelect'
import { RequiredMark } from '~/components/common/RequiredMark'

type Props = {
  /** 日付非依存の登録済区分。エリア情報だけは表示受渡日時点。 */
  options: IssuerOption[]
  /** 選択中の選択肢。 未選択は `undefined`。 */
  selected: IssuerOption | undefined
  onChange: (next: LicenseType) => void
  /**
   * 見えるラベル「発注主体」(必須マーク付き) を描かない (select の aria-label は残す)。
   * 開閉式ツールバーの [[FilterGrid]] がラベルを出す時に、二重表示を避けるために使う。
   */
  hideLabel?: boolean
}

/**
 * 画面上部ツールバーの発注主体区分セレクタ (spec AC-BSP-005d「画面上部のツールバー」、
 * design `bidding/spot.html` `#spot-page-issuer-selector`)。
 *
 * いまどの発注主体区分で札を作るかを常に画面に出す (#7075)。 既定は選択肢の先頭
 * (= 需要BG＞発電＞自己託送BG の優先順で登録済みの最上位)。 選択肢・既定選択の規則は
 * モーダルの [[IssuerSelector]] と同じ優先順で [[useRegisteredIssuerSelection]] だが、 state の持ち主 (ページ)
 * が呼ぶ。 選択肢・既定選択の補正は表示部品の mount に依らない。 本部品は描くだけ。
 *
 * select の右に、 選択中の区分で発注できるエリアを出す (design `bid-issuer-status`)。
 * 区分が 1 つも登録されていない時は select を出さず、 案内文
 * (「発注可能な発注主体区分が登録されていません…」) を出す。 区分 0 件の時は入札一覧の
 * 空状態が出ないので、 モーダルを開く前に分かる場所はツールバーだけになる。
 */
export const PageIssuerSelector = ({
  options,
  selected,
  onChange,
  hideLabel = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const label = t('page:biddingCommon.issuerSelector.label')

  return (
    <HStack alignItems="center" flexShrink={0} gap={2}>
      {!hideLabel && (
        <Text textStyle="label" color="Text.highEmphasis" whiteSpace="nowrap">
          {label}
          <RequiredMark />
        </Text>
      )}
      {options.length === 0 ? (
        <Text color="Text.midEmphasis" fontSize="sm">
          {t('page:biddingCommon.issuerSelector.noAccount')}
        </Text>
      ) : (
        <>
          {/* 幅は選択の前後で変えない。 最長の 「自己託送 BG」 が Windows の Meiryo でも
              収まる幅に固定する。 */}
          <Box flexShrink={0} width="160px">
            <DsSelect
              width="100%"
              aria-label={label}
              onChange={(next) => {
                onChange(
                  next === undefined || next === ''
                    ? LicenseType.UNSPECIFIED
                    : (Number(next) as LicenseType),
                )
              }}
              options={options.map((o) => ({
                value: String(o.issuerType),
                label: issuerTypeLabel(o.issuerType),
              }))}
              placeholder={t('common:form.selectPlaceholder')}
              value={selected !== undefined ? String(selected.issuerType) : ''}
            />
          </Box>
          <IssuerAreaStatus marginTop={0} selected={selected} />
        </>
      )}
    </HStack>
  )
}
